import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, ClipboardCheck, Printer, ChevronRight, Mail, UserCircle, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import SwimLevelBadge from "@/components/SwimLevelBadge";
import ColorTestOverlay from "@/components/ColorTestOverlay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Swim colors in order from lowest to highest
const SWIM_COLORS = ["Green", "Yellow", "Green / Blue", "Red", "Yellow / Blue", "Red / Blue"];

// Swim levels options
const SWIM_LEVELS = ["-", "PSA1", "PSA2", "1", "2", "3", "4", "5", "6"];

// Level rank for comparison
const LEVEL_RANK = {
  "Green": 0,
  "Yellow": 1,
  "Green / Blue": 2,
  "Green/Blue": 2,
  "Red": 3,
  "Yellow / Blue": 4,
  "Yellow/Blue": 4,
  "Red / Blue": 5,
  "Red/Blue": 5,
  "-": 0,
};

// Color styles
const COLOR_STYLES = {
  "Green": "text-green-400",
  "Yellow": "text-yellow-400", 
  "Green / Blue": "text-teal-400",
  "Red": "text-red-400",
  "Yellow / Blue": "text-blue-400",
  "Red / Blue": "text-blue-500",
};

export default function CamperDetailModal({ camper, onClose, onUpdate, onDelete }) {
  const [showColorTest, setShowColorTest] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentColor, setCurrentColor] = useState(camper.swim_color || "Green");
  const [currentLevel, setCurrentLevel] = useState(camper.swim_level || "-");

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/campers/${camper.id}`);
      toast.success(`Deleted ${camper.first_name} ${camper.last_name}`);
      if (onDelete) onDelete(camper.id);
      onClose();
    } catch (error) {
      toast.error("Failed to delete camper");
      console.error(error);
    }
    setDeleting(false);
  };

  const handleLevelChange = async (newLevel) => {
    if (newLevel === currentLevel) return;
    
    try {
      setUpdating(true);
      const response = await axios.patch(`${API}/campers/${camper.id}`, {
        swim_level: newLevel === "-" ? "" : newLevel
      });

      const updatedCamper = response.data;
      setCurrentLevel(updatedCamper.swim_level || "-");
      toast.success(`Swim level changed to ${newLevel}`);

      if (onUpdate) {
        onUpdate(updatedCamper);
      }
    } catch (error) {
      console.error("Error updating level:", error);
      toast.error("Failed to update level: " + (error.response?.data?.detail || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleColorChange = async (newColor) => {
    if (newColor === currentColor) return;

    const oldRank = LEVEL_RANK[currentColor] || 0;
    const newRank = LEVEL_RANK[newColor] || 0;
    const isDowngrade = newRank < oldRank;

    // Confirm if downgrading
    if (isDowngrade) {
      const confirmed = window.confirm(
        `Are you sure you want to change ${camper.first_name}'s color from ${currentColor} to ${newColor}?\n\nThis will CLEAR all their test results.`
      );
      if (!confirmed) return;
    }

    try {
      setUpdating(true);
      const response = await axios.patch(`${API}/campers/${camper.id}`, {
        swim_color: newColor
      });

      const updatedCamper = response.data;
      setCurrentColor(updatedCamper.swim_color || newColor);
      
      if (isDowngrade) {
        toast.success(`Color changed to ${newColor}. Tests cleared.`);
      } else {
        toast.success(`Color changed to ${newColor}`);
      }

      // Update parent component with new camper data from server
      if (onUpdate) {
        onUpdate(updatedCamper);
      }
    } catch (error) {
      console.error("Error updating color:", error);
      toast.error("Failed to update color: " + (error.response?.data?.detail || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handlePrintResults = async () => {
    try {
      setPrinting(true);
      const response = await axios.get(
        `${API}/print-test-results/${camper.id}`,
        { responseType: "blob" }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${camper.full_name || camper.first_name}_test_results.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Test results PDF downloaded");
    } catch (error) {
      console.error("Error printing results:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setPrinting(false);
    }
  };

  const handleTestComplete = (updatedCamper) => {
    if (updatedCamper) {
      setCurrentColor(updatedCamper.swim_color || currentColor);
      onUpdate(updatedCamper);
    }
    setShowColorTest(false);
  };

  // If color test overlay is open, show that instead of the detail modal
  if (showColorTest) {
    return (
      <ColorTestOverlay
        camper={{ ...camper, swim_color: currentColor }}
        onClose={() => setShowColorTest(false)}
        onComplete={handleTestComplete}
        onBack={() => setShowColorTest(false)}
      />
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md bg-slate-900 border-slate-800 text-white"
        data-testid="camper-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-['Manrope']">
            <div className="h-12 w-12 rounded-full bg-slate-800 flex items-center justify-center">
              <User className="h-6 w-6 text-slate-400" />
            </div>
            <div>
              <div className="text-xl">
                {camper.full_name || `${camper.first_name} ${camper.last_name}`}
              </div>
              <div className="text-sm font-normal text-slate-400">
                {camper.bunk && <span className="text-cyan-400 font-medium">{camper.bunk}</span>}
                {camper.bunk && camper.division && " • "}
                {camper.division && <span>{camper.division}</span>}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Swim Info */}
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-slate-400 mb-1">Swim Level</div>
                <Select 
                  value={currentLevel} 
                  onValueChange={handleLevelChange}
                  disabled={updating}
                >
                  <SelectTrigger 
                    className="w-[100px] bg-slate-700 border-slate-600 text-white"
                    data-testid="swim-level-select"
                  >
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {SWIM_LEVELS.map((level) => (
                      <SelectItem 
                        key={level} 
                        value={level}
                        className="text-white hover:bg-slate-700 focus:bg-slate-700"
                      >
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-400 mb-1">Current Color</div>
                <SwimLevelBadge color={currentColor} size="lg" />
              </div>
            </div>
            
            {/* Color Change Buttons */}
            <div className="border-t border-slate-700 pt-4">
              <div className="text-sm text-slate-400 mb-2">Change Color:</div>
              <div className="flex flex-wrap gap-2">
                {SWIM_COLORS.map((color) => {
                  const isCurrentColor = color === currentColor;
                  const isDowngrade = LEVEL_RANK[color] < LEVEL_RANK[currentColor];
                  return (
                    <button
                      key={color}
                      onClick={() => !isCurrentColor && handleColorChange(color)}
                      disabled={updating || isCurrentColor}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        isCurrentColor
                          ? 'bg-slate-600 text-white cursor-default ring-2 ring-white'
                          : isDowngrade
                          ? 'bg-slate-700 hover:bg-orange-600/50 text-orange-300 border border-orange-500/50'
                          : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                      } ${updating ? 'opacity-50' : ''}`}
                      data-testid={`color-btn-${color.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {color}
                      {isDowngrade && !isCurrentColor && " ⚠️"}
                    </button>
                  );
                })}
              </div>
              {updating && <div className="text-sm text-slate-400 mt-2">Updating...</div>}
            </div>
          </div>

          {/* Parent/Guardian Info - Read-only, synced from Campminder */}
          {(camper.parent_name || camper.parent_email) && (
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 space-y-3">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <UserCircle className="h-4 w-4" />
                Parent/Guardian
              </h3>
              
              {/* Primary Parent */}
              <div className="space-y-1">
                {camper.parent_name && (
                  <div className="text-white font-medium">
                    {camper.parent_name}
                  </div>
                )}
                
                {camper.parent_email && (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${camper.parent_email}`} className="text-sky-400 hover:underline">
                      {camper.parent_email}
                    </a>
                  </div>
                )}
              </div>
              
              {/* Second Parent (if exists) */}
              {(camper.parent2_name || camper.parent2_email) && (
                <div className="space-y-1 pt-2 border-t border-slate-700">
                  {camper.parent2_name && (
                    <div className="text-white font-medium">
                      {camper.parent2_name}
                    </div>
                  )}
                  
                  {camper.parent2_email && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${camper.parent2_email}`} className="text-sky-400 hover:underline">
                        {camper.parent2_email}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => setShowColorTest(true)}
              className="w-full h-14 bg-sky-600 hover:bg-sky-500 text-white font-semibold text-base justify-between"
              data-testid="color-tests-btn"
            >
              <div className="flex items-center gap-3">
                <ClipboardCheck className="h-5 w-5" />
                Color Tests
              </div>
              <ChevronRight className="h-5 w-5" />
            </Button>

            <Button
              onClick={handlePrintResults}
              disabled={printing}
              variant="outline"
              className="w-full h-14 border-slate-700 text-slate-200 hover:bg-slate-800 font-semibold text-base justify-between"
              data-testid="print-results-btn"
            >
              <div className="flex items-center gap-3">
                <Printer className="h-5 w-5" />
                {printing ? "Generating PDF..." : "Print Color Test Results"}
              </div>
              <ChevronRight className="h-5 w-5" />
            </Button>

            {/* Delete Camper Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-12 border-red-800 text-red-400 hover:bg-red-900/30 hover:text-red-300 font-medium text-sm justify-center gap-2"
                  data-testid="delete-camper-btn"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Camper
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-700">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Delete {camper.first_name} {camper.last_name}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    This will permanently delete this camper and all their test results. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
